import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import MoneyInput from '../components/MoneyInput'
import { groupEstimateFacts } from '../lib/estimates'
import { formatMoney, isZeroMoney } from '../lib/money'
import type { CostRow } from '../types'

interface Props {
  open: boolean
  row: CostRow | null
  /** All budget rows; the group's items are derived from them. */
  rows: CostRow[]
  currency: string
  locale: string
  onClose: () => void
  /** A new total, or null to remove the estimate. Resolves once the write is done; the caller closes the dialog on success. */
  onSave: (amount: string | null) => Promise<void>
}

const GroupEstimateDialog: React.FC<Props> = ({ open, row, rows, currency, locale, onClose, onSave }) => {
  const { t } = useTranslation()
  const facts = useMemo(() => (row ? groupEstimateFacts(rows, row.id) : null), [rows, row])
  const hasCurrent = !!facts && !isZeroMoney(facts.current)
  const [amount, setAmount] = useState<string | null>(null)
  const [valid, setValid] = useState(true)
  const [pending, setPending] = useState<'save' | 'clear' | null>(null)
  useEffect(() => {
    setAmount(hasCurrent ? facts!.current : null)
    setValid(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id, open])

  if (!row || !facts) return null
  const money = (v: string) => formatMoney(v, { locale, currency })

  const submit = async (kind: 'save' | 'clear') => {
    setPending(kind)
    try {
      await onSave(kind === 'clear' ? null : amount)
    } finally {
      setPending(null)
    }
  }
  const busy = pending !== null

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t('CostControl_estimate_group_title')} · {row.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('CostControl_estimate_group_hint')}</p>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('CostControl_estimate_group_items', { count: facts.itemCount })}</dt></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('CostControl_estimate_group_budget')}</dt><dd className="tabular-nums">{money(facts.budget)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('CostControl_estimate_group_current')}</dt><dd className="tabular-nums">{money(facts.current)}</dd></div>
          </dl>
          <div className="space-y-2">
            <Label>{t('CostControl_estimate_amount')}</Label>
            <MoneyInput value={amount} onChange={(v, ok) => { setAmount(v); setValid(ok) }} disabled={busy || facts.itemCount === 0} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('Common_cancel')}</Button>
          {hasCurrent && (
            <Button variant="outline" onClick={() => { void submit('clear') }} disabled={busy}>
              {pending === 'clear' && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('CostControl_estimate_clear')}
            </Button>
          )}
          <Button onClick={() => { void submit('save') }} disabled={busy || facts.itemCount === 0 || !valid || amount === null}>
            {pending === 'save' && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_estimate_save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default GroupEstimateDialog
